import { gql } from "@apollo/client";
import { EPlayedGames } from "../@types";

const ALLSTAR_INGAME_PARTNER_ID = "6a43108b1fc640027edf561a";

export const HOMEPAGE_CLIP_FIELDS_NEW = gql`
  fragment HomepageClipFieldsNew on Clip {
    thumbnailStandardUrl: thumbnailUrl(style: STANDARD)
    thumbnailOverlayUrl: thumbnailUrl(style: OVERLAY)
    previewUrl
    title
    username
    createdAt
    shareId
    views
    score
    playerGameIdentifier
    tags {
      key
      value
    }
    user {
      _id
      avatarUrl
      username
      rank
      userGroup {
        type
      }
    }
  }
`;

export const GET_FRIENDS_LEADERBOARD = gql`
  query FriendsLeaderboard($limit: Int) {
    me {
      friendsLeaderboard(limit: $limit) {
        rank
        score
        user {
          id
          username
          avatarUrl
        }
      }
    }
  }
`;

export const GET_TRENDING_CLIPS = gql`
  query TrendingClips($limit: Int) {
    clipsNew(first: $limit, sort: POPULAR, filters: { game: CS2 }) {
      nodes {
        id
        shareId
        title
        username
        views
        thumbnailUrl(style: STANDARD)
      }
    }
  }
`;

export const HOMEPAGE_CONTENT_FEED = (recommended: boolean) => gql`
  ${HOMEPAGE_CLIP_FIELDS_NEW}

  query HomepageContentFeed($limit: Int) {
    montages: montagesNew(first: $limit, sort: LATEST) {
      nodes {
        ...HomepageClipFieldsNew
      }
    }

    forYou: ${
      recommended
        ? "clipsRecommended(limit: $limit)"
        : "clipsNew(first: $limit)"
    } {
      ${
        recommended
          ? "...HomepageClipFieldsNew"
          : "nodes { ...HomepageClipFieldsNew }"
      }
    }

    cs2: clipsNew(first: $limit, filters: { game: ${
      EPlayedGames.CS2
    }}, sort: LATEST, orientation: LANDSCAPE) {
      nodes {
        ...HomepageClipFieldsNew
      }
    }

    allstar: clipsNew(first: $limit, filters: { ${
      recommended ? "" : `users: ["${ALLSTAR_INGAME_PARTNER_ID}"], `
    }clipProperties: [{ key: "CS_Command", value: "!allstar" }] }, sort: LATEST) {
      nodes {
        ...HomepageClipFieldsNew
      }
    }

    fragmovies: clipsNew(first: $limit, filters: { clipProperties: [{ key: "Clip Type", value: "Fragmovie" }] }, sort: LATEST) {
      nodes {
        ...HomepageClipFieldsNew
      }
    }
  }
`;

// title is aliased because Clip.title (String, via HomepageClipFieldsNew)
// and Competition.title (String!) collide as the same response key under
// the shared `item` union selection — GraphQL requires identical response
// shape (including nullability) for same-named fields there, even though
// Clip and Competition can never be the same concrete object.
// prizes/entryRequirement/submissionRule are cheap stored JSON (no
// aggregation), so unlike leaderboard (score computation — see the lazy
// extras query below) they're fine to fetch eagerly for every competition
// card; they power the rule chips and prize teaser across all three states
// (upcoming/active/completed), not just completed ones.
const ACTIVITY_FEED_COMPETITION_FIELDS = gql`
  fragment ActivityFeedCompetitionFields on Competition {
    id
    _id
    competitionTitle: title
    bannerGraphic
    startsAt
    endsAt
    participantCount
    prizes
    entryRequirement
    submissionRule
    creatorUser {
      id
      username
      avatarUrl
      userGroup {
        type
      }
    }
  }
`;

// The bulk feed query intentionally stays light — video url, reactions,
// and leaderboard only ever matter for the one card currently autoplaying
// (clips) or a completed competition's winners row, so they're loaded lazily
// per-card via GET_ACTIVITY_FEED_CLIP_EXTRAS /
// GET_ACTIVITY_FEED_COMPETITION_EXTRAS. Reactions and comment count are the
// exception: the stat bar renders on every clip card, not just the active
// one, so fetching them lazily would show 0/0 on all but three cards.
export const ACTIVITY_FEED = gql`
  ${HOMEPAGE_CLIP_FIELDS_NEW}
  ${ACTIVITY_FEED_COMPETITION_FIELDS}

  query ActivityFeed($seed: String!, $first: Int, $after: String) {
    activityFeed(seed: $seed, first: $first, after: $after) {
      pageInfo {
        hasNextPage
        endCursor
      }
      nodes {
        reason
        occurredAt
        item {
          __typename
          ... on Clip {
            ...HomepageClipFieldsNew
            id
            internalId
            game
            duration
            promotedCompetition {
              _id
              title
              bannerGraphic
              endsAt
              participantCount
              prizes
            }
            reactions {
              type
              count
            }
            comments(first: 0, excludeDeleted: true) {
              pageInfo {
                totalCount
              }
            }
          }
          ... on Competition {
            ...ActivityFeedCompetitionFields
          }
          ... on BlogPost {
            id
            # aliased — BlogPost.title (String!) collides with Clip.title
            # (String) under the shared item union selection, same issue
            # ActivityFeedCompetitionFields' competitionTitle alias avoids.
            postTitle: title
            slug
            author
            avatarUrl
            nickname
            publishedDate
          }
        }
      }
    }
  }
`;

export const GET_ACTIVITY_FEED_CLIP_EXTRAS = gql`
  query ActivityFeedClipExtras($id: String!) {
    clip(id: $id) {
      id
      url(orientation: LANDSCAPE)
    }
  }
`;

export const GET_ACTIVITY_FEED_COMPETITION_EXTRAS = gql`
  query ActivityFeedCompetitionExtras($id: String!) {
    competition(id: $id) {
      id
      leaderboard(limit: 3) {
        topEntries {
          rank
          score
          submittedClipCount
          qualified
          userId
          user {
            id
            username
            avatarUrl
          }
        }
      }
    }
  }
`;
